<template>
    <div class="tiptap-viewer">
        <div :class="{'content-preview': isLongContent && !expanded}">
            <editor-content :editor="viewer" ref="editorContent" />
        </div>
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Highlight from '@tiptap/extension-highlight'
import TextAlign from '@tiptap/extension-text-align'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'

export default {
    components: {
        EditorContent,
    },

    props: {
        content: {
            type: String,
            default: '',
        },
        maxLines: {
            type: Number,
            default: 5
        },
        expanded: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            viewer: null,
            lineCount: 0
        };
    },

    computed: {
        isLongContent() {
            return this.lineCount > this.maxLines;
        }
    },

    watch: {
        content(newContent) {
            // Update the editor content when the prop changes
            if (this.viewer && newContent !== this.viewer.getHTML()) {
                this.viewer.commands.setContent(newContent, false);
                this.$nextTick(this.calculateLineCount);
            }
        },
        
        // Add a watch for expanded prop
        expanded() {
            // Force a rerender when expanded changes
            this.$nextTick(() => {
                if (this.viewer) {
                    this.viewer.view.focus();
                    this.viewer.view.dom.blur();
                }
            });
        }
    },

    mounted() {
        this.viewer = new Editor({
            extensions: [
                StarterKit.configure({
                    heading: {
                        levels: [1, 2, 3],
                    }
                }),
                Underline,
                Highlight.configure({
                    multicolor: true,
                }),
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
                TaskList,
                TaskItem.configure({
                    nested: true,
                }),
                Table.configure({
                    resizable: true,
                }),
                TableRow,
                TableHeader,
                TableCell,
            ],
            content: this.content,
            editable: false, // This makes it read-only
            onTransaction: () => {
                // This ensures the content is properly initialized
                this.$nextTick(this.calculateLineCount);
            }
        });

        // Use nextTick instead of setTimeout for Vue best practices
        this.$nextTick(this.calculateLineCount);
    },

    beforeDestroy() {
        if (this.viewer) {
            this.viewer.destroy();
        }
    },

    methods: {
        calculateLineCount() {
            this.lineCount = this.getDisplayedLineCount();
            
            // Emit long-content event when needed
            const isLong = this.lineCount > this.maxLines;
            this.$emit('long-content', isLong);
        },

        /**
         * Counts the number of displayed lines in the viewer
         * This accounts for wrapped text and multiple elements
         * @returns {number} The number of visible lines
         */
        getDisplayedLineCount() {
            if (!this.viewer) return 0;
            
            // Get the editor element
            const viewerElement = this.viewer.options.element;
            if (!viewerElement) return 0;
            
            // Find the ProseMirror element
            const proseMirror = viewerElement.querySelector('.ProseMirror');
            if (!proseMirror) return 0;
            
            // Get all block-level elements (including tables)
            const blockElements = proseMirror.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, div, table');
            
            let totalLines = 0;
            
            // Process each block element
            blockElements.forEach(element => {
                // Special handling for tables
                if (element.tagName.toLowerCase() === 'table') {
                    // Count each table row as a line
                    const rows = element.querySelectorAll('tr');
                    totalLines += rows.length * 2; // Add extra lines for tables as they take more space
                    return;
                }
                
                // Calculate how many lines this element takes up
                const elementHeight = element.offsetHeight;
                const computedStyle = window.getComputedStyle(element);
                const lineHeight = parseInt(computedStyle.lineHeight) || parseInt(computedStyle.fontSize) * 1.2;
                
                // Calculate number of lines (round up to account for partial lines)
                const linesInElement = Math.ceil(elementHeight / lineHeight);
                totalLines += linesInElement;
            });
            
            return totalLines;
        },
    },
};
</script>

<style scoped>
.tiptap-viewer {
    font-size: 16px;
    white-space: pre-line;
}

.content-preview {
    max-height: calc(1.2em * 10); /* Approximate height for 10 lines of text */
    overflow: hidden;
    position: relative;
}

.content-preview::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3em;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
    pointer-events: none;
}

::v-deep .ProseMirror {
    outline: none;
    padding: 0;
}

::v-deep .ProseMirror p {
    margin-top: 0;
    margin-bottom: 0.1em;
}

/* Task lists styling */
::v-deep ul[data-type="taskList"] {
    list-style: none;
    padding: 0;
    margin-left: 0;
}

::v-deep ul[data-type="taskList"] li {
    display: flex;
    align-items: flex-start;
    margin-bottom: 0.5em;
}

::v-deep ul[data-type="taskList"] li > label {
    flex: 0 0 auto;
    margin-right: 0.5em;
    user-select: none;
}

::v-deep ul[data-type="taskList"] li > div {
    flex: 1 1 auto;
}

/* Removed strikethrough styling */
::v-deep ul[data-type="taskList"] li[data-checked="true"] > div {
    opacity: 1;
}

::v-deep ul[data-type="taskList"] ul[data-type="taskList"] {
    margin: 0.5em 0 0.5em 1.5em;
}

/* Table styles */
::v-deep .ProseMirror table {
    border-collapse: collapse;
    margin: 0;
    overflow: hidden;
    table-layout: fixed;
    width: 100%;
    margin: 1em 0;
}

::v-deep .ProseMirror table td,
::v-deep .ProseMirror table th {
    border: 1px solid rgba(0, 0, 0, 0.2);
    box-sizing: border-box;
    min-width: 1em;
    padding: 8px;
    position: relative;
    vertical-align: top;
}

::v-deep .ProseMirror table th {
    background-color: #f6f8fa;
    font-weight: bold;
}

::v-deep .ProseMirror table p {
    margin: 0;
}

::v-deep .tableWrapper {
    overflow-x: auto;
    padding: 1em 0;
}
</style> 